import { useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { POSTS } from "@/data/blogPosts";
import { useSeo, SITE_URL } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ArrowLeft, Lightbulb, CheckCircle2, Home } from "lucide-react";

const buildLd = (p) => [
  {
    "@context": "https://schema.org", "@type": "BlogPosting", headline: p.title, description: p.description,
    datePublished: p.date, dateModified: p.date, url: `${SITE_URL}/blog/${p.slug}`, keywords: p.keyword,
    author: { "@type": "Organization", name: "InspectorProLeads" },
    publisher: { "@type": "Organization", name: "InspectorProLeads", url: SITE_URL },
    about: p.keyword, spatialCoverage: { "@type": "Place", name: p.area },
  },
  { "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: p.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
  { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
    { "@type": "ListItem", position: 3, name: p.title, item: `${SITE_URL}/blog/${p.slug}` }] },
];

const Section = ({ s }) => (
  <section className="mt-10">
    <h2 className="font-display text-2xl font-bold text-[color:var(--navy)]">{s.h}</h2>
    {s.p.map((t, i) => <p key={i} className="text-[color:var(--ink-2)] leading-relaxed mt-4">{t}</p>)}
    {s.list && <ul className="mt-4 space-y-2">{s.list.map((l) => (
      <li key={l} className="flex gap-2 text-[color:var(--ink-2)]"><CheckCircle2 className="w-5 h-5 text-[color:var(--emerald)] shrink-0 mt-0.5" /> <span>{l}</span></li>))}</ul>}
  </section>
);

function Article({ p }) {
  const nav = useNavigate();
  const ld = useMemo(() => buildLd(p), [p]);
  useSeo({ title: p.seoTitle, description: p.description, path: `/blog/${p.slug}`, jsonLd: ld, type: "article" });
  return (
    <article className="container-x py-14 max-w-3xl" data-testid="blog-post">
      <Link to="/blog" className="text-sm text-[color:var(--ink-3)] flex items-center gap-1 hover:text-[color:var(--emerald)]" data-testid="blog-back-link"><ArrowLeft className="w-4 h-4" /> All guides</Link>
      <span className="inline-block text-xs font-mono uppercase tracking-wider badge-pest px-3 py-1 rounded-full mt-6">{p.tag}</span>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[color:var(--navy)] mt-3 leading-tight" data-testid="blog-post-title">{p.title}</h1>
      <p className="text-sm text-[color:var(--ink-3)] mt-4">By the InspectorProLeads team · Updated {new Date(p.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} · {p.readMinutes} min read</p>

      <div className="card-soft p-6 mt-8 border-l-4 border-l-[color:var(--emerald)]" data-testid="blog-quick-answer">
        <div className="flex items-center gap-2 font-semibold text-[color:var(--navy)]"><Lightbulb className="w-5 h-5 text-amber-500" /> Quick answer</div>
        <p className="text-[color:var(--ink-2)] mt-2 leading-relaxed">{p.quickAnswer}</p>
      </div>

      <p className="text-lg text-[color:var(--ink-2)] leading-relaxed mt-8">{p.intro}</p>
      {p.sections.map((s) => <Section key={s.h} s={s} />)}

      <section className="mt-12 rounded-3xl bg-[color:var(--bg-3)] p-8" data-testid="blog-homeowner-tips">
        <div className="flex items-center gap-2"><Home className="w-5 h-5 text-[color:var(--emerald)]" /><h2 className="font-display text-2xl font-bold text-[color:var(--navy)]">{p.homeowners.h}</h2></div>
        {p.homeowners.p.map((t, i) => <p key={i} className="text-[color:var(--ink-2)] leading-relaxed mt-4">{t}</p>)}
        <ul className="mt-4 space-y-2">{p.homeowners.list.map((l) => (
          <li key={l} className="flex gap-2 text-[color:var(--ink-2)]"><CheckCircle2 className="w-5 h-5 text-[color:var(--emerald)] shrink-0 mt-0.5" /> <span>{l}</span></li>))}</ul>
        <Button onClick={() => nav("/quote")} className="btn-amber rounded-full mt-6 font-semibold" data-testid="blog-homeowner-cta">Get a free quote</Button>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold text-[color:var(--navy)]">Key takeaways</h2>
        <ul className="mt-4 space-y-2">{p.takeaways.map((t) => (
          <li key={t} className="flex gap-2 text-[color:var(--ink-2)]"><CheckCircle2 className="w-5 h-5 text-[color:var(--emerald)] shrink-0 mt-0.5" /> <span>{t}</span></li>))}</ul>
      </section>

      <section className="mt-12" data-testid="blog-faq">
        <h2 className="font-display text-2xl font-bold text-[color:var(--navy)]">Frequently asked questions</h2>
        <Accordion type="multiple" className="mt-4">
          {p.faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`f${i}`} data-testid={`blog-faq-${i}`}>
              <AccordionTrigger className="text-left font-semibold text-[color:var(--navy)]">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[color:var(--ink-2)] leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <div className="mt-14 rounded-3xl bg-[color:var(--navy)] text-white p-10 text-center" data-testid="blog-pro-cta">
        <h2 className="font-display text-3xl font-bold">Ready for more local jobs?</h2>
        <p className="text-slate-300 mt-3 max-w-lg mx-auto">Founding Membership: 60 days free, then $15/month, locked for 12 months. Only 25 founding spots.</p>
        <Button onClick={() => nav("/pricing")} className="btn-amber rounded-full mt-6 font-semibold px-8" data-testid="blog-pro-cta-button">Start my free 60 days</Button>
      </div>
    </article>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const p = POSTS.find((x) => x.slug === slug);
  if (!p) return (
    <div className="container-x py-24 text-center" data-testid="blog-not-found">
      <h1 className="font-display text-3xl font-bold text-[color:var(--navy)]">Guide not found</h1>
      <Link to="/blog" className="text-[color:var(--emerald)] mt-4 inline-block">Back to all guides</Link>
    </div>
  );
  return <Article p={p} />;
}
